import useFirestore from "./useFirestore";
import { STATIC_FEATURE_UPDATES } from "../static/updates/featureUpdates";
import { useMemo } from "react";

export const useFeatureUpdates = () => {
  const { userSettings, isLoadingUser, updateUserSettings } = useFirestore();

  const isReady = !isLoadingUser && userSettings != null;

  const allUpdates = useMemo(() => {
    let filtered = STATIC_FEATURE_UPDATES;
    if (userSettings?.crmType !== "realEstate") {
      filtered = filtered.filter((update) => !update.showOnlyForRealEstate);
    }
    return filtered.sort((a, b) => b.id - a.id);
  }, [userSettings?.crmType]);

  const unseenUpdates = useMemo(() => {
    if (!isReady) return [];
    const lastSeenUpdateId = userSettings?.lastSeenUpdateId ?? 0;
    return allUpdates.filter((update) => update.id > lastSeenUpdateId);
  }, [isReady, allUpdates, userSettings?.lastSeenUpdateId]);

  // Seen up to and including `updateId`. A popup marks its own update, never a newer one the user has not opened.
  const markSeenThrough = async (updateId: number) => {
    const lastSeenUpdateId = userSettings?.lastSeenUpdateId ?? 0;

    if (updateId > lastSeenUpdateId) {
      try {
        await updateUserSettings({ lastSeenUpdateId: updateId });
      } catch (error) {
        console.error("Failed to update last seen update ID", error);
      }
    }
  };

  const markAllAsSeen = () => markSeenThrough(Math.max(...allUpdates.map((u) => u.id), 0));

  return {
    allUpdates,
    unseenUpdates,
    hasUpdates: allUpdates.length > 0,
    hasUnseenUpdates: unseenUpdates.length > 0,
    markAllAsSeen,
    markSeenThrough
  };
};
